import React, { useState } from 'react';
import { FiCheck, FiShield, FiImage, FiInfo } from 'react-icons/fi';

export default function OnboardingConsent({ onAccept, userName }) {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    if (!agreed) return;
    setLoading(true);
    setError('');
    try {
      const success = await onAccept();
      if (!success) {
        setError('Failed to save consent. Please try again or refresh the page.');
      }
    } catch (error) {
      console.error('Error accepting consent:', error);
      setError('Something went wrong. Please refresh the page and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxToggle = () => {
    setAgreed(prev => !prev);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="onboarding-consent-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600/20 to-fuchsia-600/20 px-6 py-8 text-center border-b border-slate-700/50">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-violet-500/25">
            <span className="text-white font-bold text-2xl">F</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Welcome to FirstCut{userName ? `, ${userName.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-slate-400">
            Let&apos;s get you set up for creating amazing content
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <p className="text-slate-300 mb-6 leading-relaxed">
            FirstCut uses AI to create professional content for your brand. Here&apos;s what you should know:
          </p>

          <div className="space-y-4 mb-6">
            {/* Point 1 */}
            <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <FiShield className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium mb-1">Industry-Standard Content Credentials</p>
                <p className="text-slate-400 text-sm">
                  Your images include C2PA credentials — the same standard used by Adobe, Google & Microsoft
                </p>
              </div>
            </div>

            {/* Point 2 */}
            <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <FiInfo className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium mb-1">Social Media Labels</p>
                <p className="text-slate-400 text-sm">
                  Platforms like Instagram may show a small &quot;AI info&quot; label — this is normal for all AI-created content industry-wide
                </p>
              </div>
            </div>

            {/* Point 3 */}
            <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
              <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <FiImage className="w-4 h-4 text-violet-400" />
              </div>
              <div>
                <p className="text-white font-medium mb-1">You Own Your Content</p>
                <p className="text-slate-400 text-sm">
                  All content you create is yours. You decide where and how to use it.
                </p>
              </div>
            </div>
          </div>

          {/* Checkbox - separated from links for reliable touch handling on iPad */}
          <div className="flex items-start gap-3 mb-6">
            <button
              type="button"
              role="checkbox"
              aria-checked={agreed}
              onClick={handleCheckboxToggle}
              data-testid="consent-checkbox"
              className="relative flex-shrink-0 mt-0.5 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded"
            >
              <div className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${
                agreed 
                  ? 'bg-violet-500 border-violet-500' 
                  : 'border-slate-500 hover:border-slate-400'
              }`}>
                {agreed && <FiCheck className="w-3 h-3 text-white" />}
              </div>
            </button>
            <span className="text-slate-300 text-sm leading-relaxed select-none">
              I&apos;ve read and agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 underline">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 underline">
                Privacy Policy
              </a>
            </span>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm" data-testid="consent-error">
              {error}
            </div>
          )}

          {/* Button */}
          <button
            onClick={handleAccept}
            disabled={!agreed || loading}
            data-testid="consent-accept-button"
            className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
              agreed && !loading
                ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 shadow-lg shadow-violet-500/25'
                : 'bg-slate-700 cursor-not-allowed opacity-50'
            }`}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Setting up...</span>
              </>
            ) : (
              <>
                <span>Start Creating</span>
                <FiCheck className="w-5 h-5" />
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-800/30 border-t border-slate-700/50">
          <p className="text-slate-500 text-xs text-center">
            Questions? Check our{' '}
            <a href="/help" className="text-slate-400 hover:text-white">Help Center</a>
            {' '}or{' '}
            <a href="/contact" className="text-slate-400 hover:text-white">Contact Support</a>
          </p>
        </div>
      </div>
    </div>
  );
}
